/**
 * PowerSync initialization for Web
 * 
 * Инициализирует PowerSync для web-приложения.
 * PowerSync использует локальную SQLite через wa-sqlite для offline-first работы.
 * 
 * PowerSync использует wa-sqlite (OPFS) для хранения данных в браузере,
 * а данные синхронизируются автоматически через PowerSync Service.
 */

import { PowerSyncDatabase } from '@powersync/web';
import { AppSchema, AlveonPowerSyncConnector, createUploadHandler } from '@alveon/shared/powersync';
import { logger } from '../utils/logger';

// Синглтон PowerSync database
let powerSyncInstance: PowerSyncDatabase | null = null;
let powerSyncReady = false;
let initPromise: Promise<void> | null = null;

/**
 * Получить экземпляр PowerSync Database.
 * Возвращает null если PowerSync ещё не инициализирован.
 */
export function getPowerSync(): PowerSyncDatabase | null {
  return powerSyncInstance;
}

/**
 * Проверить, готов ли PowerSync к работе
 */
export function isPowerSyncReady(): boolean {
  return powerSyncReady;
}

/**
 * Инициализация PowerSync для веб.
 * 
 * @param getAuthToken - функция для получения текущего auth токена
 * @param apiBaseUrl - базовый URL API сервера
 */
export async function initializePowerSync(
  getAuthToken: () => Promise<string | null>,
  apiBaseUrl: string,
  apiClientRef?: any
): Promise<PowerSyncDatabase | null> {
  // Защита от множественных вызовов
  if (initPromise) {
    await initPromise;
    return powerSyncInstance;
  }

  initPromise = _doInitialize(getAuthToken, apiBaseUrl, apiClientRef);
  await initPromise;
  return powerSyncInstance;
}

async function _doInitialize(
  getAuthToken: () => Promise<string | null>,
  apiBaseUrl: string,
  apiClientRef?: any
): Promise<void> {
  try {
    logger.info('[PowerSync] Initializing for web...');

    // Создаём PowerSync Database для веб (использует wa-sqlite + OPFS)
    powerSyncInstance = new PowerSyncDatabase({
      schema: AppSchema,
      database: {
        dbFilename: 'alveon-powersync.db',
      },
    });

    logger.info('[PowerSync] Database created');

    // Создаём коннектор
    const connector = new AlveonPowerSyncConnector({
      powersyncUrl: '', // Будет получен из fetchPowerSyncToken
      
      getToken: getAuthToken,

      fetchPowerSyncToken: async () => {
        const token = await getAuthToken();
        if (!token) {
          throw new Error('No auth token available for PowerSync');
        }

        // Запрашиваем PowerSync credentials с бэкенда
        const response = await fetch(`${apiBaseUrl}/api/powersync/credentials`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`PowerSync credentials failed: ${response.status} ${errorText}`);
        }

        const data = await response.json();
        
        // Обновляем powersyncUrl из ответа сервера
        (connector as any).config.powersyncUrl = data.powersync_url;

        return {
          token: data.token,
          expiresAt: data.expires_at ? new Date(data.expires_at) : undefined,
        };
      },

      uploadCrudEntry: createUploadHandler(apiClientRef, getPowerSync),
    });

    // Подключаемся к PowerSync Service
    await powerSyncInstance.connect(connector);
    
    powerSyncReady = true;
    logger.info('[PowerSync] Connected and syncing!');

  } catch (error) {
    logger.error('[PowerSync] Initialization failed:', error);
    powerSyncReady = false;
    // Не бросаем ошибку — PowerSync опционален
  }
}

/**
 * Отключить PowerSync
 */
export async function disconnectPowerSync(): Promise<void> {
  if (powerSyncInstance) {
    try {
      await powerSyncInstance.disconnect();
      powerSyncReady = false;
      logger.info('[PowerSync] Disconnected');
    } catch (error) {
      logger.warn('[PowerSync] Disconnect error:', error);
    }
  }
}
